/**
 * Checks if all level modules are completed
 *
 * @param {Array} builderModules - array of {module: builder_module_ref}
 * @param {Array} sfccModules - array of level modules in the learning center data (SFCC_Profile.learningCenterData attribute JSON)
 * @returns {boolean} true|false
 */
const areAllModulesComplete = (builderModules, sfccModules) => {
    for (let i = 0; i < builderModules.length; i++) {
        const builderModuleID = builderModules[i].module.value.data.id
        const sfccModule = sfccModules.find((module) => module.id === builderModuleID)

        if (!sfccModule?.complete) {
            return false
        }
    }

    return true
}

/**
 * Checks if all goal levels are completed
 *
 * @param {Array} builderLevels - array of {level: builder_level_ref}
 * @param {Array} sfccLevels - array of goal levels in the learning center data (SFCC_Profile.learningCenterData attribute JSON)
 * @returns {boolean} true|false
 */
const areAllLevelsComplete = (builderLevels, sfccLevels) => {
    for (let i = 0; i < builderLevels.length; i++) {
        const builderLevelID = builderLevels[i].level.value.data.id
        const sfccLevel = sfccLevels.find((level) => level.id === builderLevelID)

        if (!sfccLevel?.complete) {
            return false
        }
    }

    return true
}

/**
 * Checks if all goal levels are completed
 *
 * @param {object} builderLevel - builder_level_ref.data (builder content)
 * @param {object} sfccLevel - level from learning center data ()
 * @returns {boolean} true|false
 */
const getNextIncompleteModuleID = (builderLevel, sfccLevel) => {
    if (!builderLevel) return null
    const builderModuleIDs = builderLevel.modules.map((builderModule) =>
        builderModule.module.value
            ? builderModule.module.value.data.id
            : builderModule.module.data.id
    )
    const sfccModules = sfccLevel?.modules || []

    for (let i = 0; i < builderModuleIDs.length; i++) {
        const builderModuleID = builderModuleIDs[i]
        const sfccModule = sfccModules.find((module) => module.id === builderModuleID)

        if (!sfccModule?.complete) {
            return builderModuleID
        }
    }

    return null
}

/**
 * Retrieves the most recently started or completed goal that is not the active goal.
 *
 * @param {object} data - The learning center data object.
 * @returns {string|null} The ID of the most recently started or completed non-active goal, or null if no valid goal exists.
 */

const getMostRecentNonActiveGoal = (data) => {
    if (!data?.goals || !Array.isArray(data.goals)) {
        return null
    }

    const {activeGoal, goals} = data
    const nonActiveGoals = goals.filter((goal) => goal.id !== activeGoal)

    if (nonActiveGoals.length === 0) {
        return null
    }

    let mostRecentGoal = null
    let mostRecentTime = null

    nonActiveGoals.forEach((goal) => {
        const startTime = new Date(goal.start).getTime()
        const completeTime = goal.complete ? new Date(goal.complete).getTime() : 0

        const recentTime = Math.max(startTime, completeTime)

        if (!mostRecentGoal || recentTime > mostRecentTime) {
            mostRecentGoal = goal
            mostRecentTime = recentTime
        }
    })

    return mostRecentGoal?.id || null
}

export {
    areAllModulesComplete,
    areAllLevelsComplete,
    getNextIncompleteModuleID,
    getMostRecentNonActiveGoal
}
